// 금천아 빌려줘 — 주문 상세 (세팅 체크리스트 · 주소 · 금액 · 타임라인 · 전환 · 메모)
//
// ⚠️ 옵션 항목의 클래스(rental-opt--{slug})와 data-option 값은 서버가 만든 슬러그를
//    그대로 쓴다 — CSS에서 개별 옵션을 잡을 수 있게 하기 위한 것이고, 슬러그의 출처는
//    services/options.py 하나다.
// ⚠️ 체크 상태는 items_checked(JSON)에 PATCH로 저장한다.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import api from "../../api";
import { useTabs } from "../../tabs/TabContext";
import AddonLinkPicker from "./AddonLinkPicker";
import CancelResolveModal from "./CancelResolveModal";
import DateEditor from "./DateEditor";
import {
  eventDateLabel,
  formatDateTime,
  formatPaymentChange,
  formatRefundBreakdown,
  formatWon,
  handoverLabel,
  refundGuide,
  scheduleLabels,
} from "./format";
import FulfillmentEditor from "./FulfillmentEditor";
import FulfillmentRows from "./FulfillmentRows";
import HandoverProgress from "./HandoverProgress";
import PickupModal from "./PickupModal";
import RentalMemo from "./RentalMemo";
import RefundSection from "./RefundSection";
import "./rental.css";

const PICKED_UP = "PICKED_UP";


export default function RentalOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dispatch } = useTabs();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPickup, setShowPickup] = useState(false);
  const [showResolve, setShowResolve] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`rental/orders/${id}/`);
      setOrder(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "주문을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // 탭 제목을 "빌려줘 · 수령인"으로 — 캘린더 일정 링크나 새로고침처럼 목록을 거치지
  // 않고 들어온 경우에도 숫자 id가 아니라 이름이 보이게 한다.
  // ⚠️ 기존 탭 시스템의 액션을 그대로 쓴다(ADD_or_ACTIVATE는 같은 path의 제목을 갱신한다).
  const receiverName = order?.receiver_name;
  useEffect(() => {
    if (!receiverName) return;
    dispatch({
      type: "ADD_or_ACTIVATE",
      payload: {
        path: `/rental/orders/${id}`,
        title: `빌려줘 · ${receiverName}`,
        icon: "📄",
      },
    });
  }, [receiverName, id, dispatch]);

  const patch = async (payload) => {
    const res = await api.patch(`rental/orders/${id}/`, payload);
    setOrder(res.data);
    return res.data;
  };

  const toggleOption = async (slug) => {
    if (!order) return;
    const next = { ...(order.items_checked || {}) };
    next[slug] = !next[slug];
    // 낙관적 갱신 — 체크박스는 즉시 반응해야 손이 안 멈춘다. 실패하면 되돌린다.
    setOrder({ ...order, items_checked: next });
    try {
      await patch({ items_checked: next });
    } catch (e) {
      setOrder(order);
      setError(e?.response?.data?.detail || "체크 상태를 저장하지 못했습니다.");
    }
  };

  const saveDates = async (payload) => {
    setBusy(true);
    setError("");
    try {
      await patch(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || "날짜를 저장하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const copyAddress = async () => {
    const full = `${order.receiver_address || ""} ${order.receiver_address_detail || ""}`.trim();
    try {
      await navigator.clipboard.writeText(full);
      setError("");
    } catch {
      setError("복사에 실패했습니다. 주소를 직접 선택해 복사해 주세요.");
    }
  };

  const runTransition = async (payload) => {
    setBusy(true);
    setError("");
    try {
      const res = await api.post(`rental/orders/${id}/transition/`, payload);
      setShowPickup(false);
      await load();
      if (res.data?.refund_error) setError(res.data.refund_error);
    } catch (e) {
      setError(e?.response?.data?.detail || "상태를 바꾸지 못했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const resolveCancel = async (payload) => {
    setBusy(true);
    setError("");
    try {
      await api.post(`rental/orders/${id}/resolve-cancel/`, payload);
      setShowResolve(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "취소로 정리하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const issueTracking = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`rental/orders/${id}/issue-tracking/`, {});
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "송장 발행에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  // 수령 완료(직접 수령·반납) — 송장 등록(방문수령) → 배송출발 → 배송중 → 배송완료.
  // 중간 실패는 서버가 200 + ok=false로 준다(앞 단계는 반영됨) — 주문을 갱신해 진행을 보여준다.
  const handover = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api.post(`rental/orders/${id}/handover/`, {});
      if (res.data?.order) setOrder(res.data.order);
      if (res.data && !res.data.ok) {
        setError(`수령 완료 중단: ${res.data.failed} — ${res.data.error}`);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "수령 완료에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const handleNext = () => {
    if (!order?.next_status) return;
    if (order.next_status === PICKED_UP) {
      setShowPickup(true);
      return;
    }
    runTransition({ next_status: order.next_status });
  };

  if (loading) return <div className="rental-page rental-empty">불러오는 중…</div>;
  if (!order) {
    return (
      <div className="rental-page">
        <div className="rental-error">{error || "주문을 찾을 수 없습니다."}</div>
        <button type="button" className="rental-back" onClick={() => navigate("/rental/orders")}>
          목록으로
        </button>
      </div>
    );
  }

  return (
    <div className="rental-page">
      <div className="rental-header">
        <h2 className="rental-title">
          {order.receiver_name || order.order_id}
          <span className={`rental-badge rental-badge--${order.status}`} style={{ marginLeft: 8 }}>
            {order.status_label}
          </span>
        </h2>
        <div className="rental-header-right">
          <button type="button" className="rental-back" onClick={() => navigate("/rental/orders")}>
            목록
          </button>
        </div>
      </div>

      {error && <div className="rental-error">{error}</div>}

      {order.needs_cancel_review && (
        <div className="rental-warn-box" style={{ marginBottom: 12 }}>
          <strong>⚠ 취소 요청 — 확인 필요</strong>
          <div className="rental-warn-reason">
            {order.cancel_detail}
            <br />
            물건이 이미 나간 뒤 취소가 감지돼 상태를 자동으로 바꾸지 않았습니다.
            회수·환불 처리를 판단한 뒤 진행하세요.
          </div>
          {order.can_resolve_cancel && (
            <button
              type="button"
              className="rental-btn-resolve"
              onClick={() => setShowResolve(true)}
              disabled={busy}
            >
              취소로 정리
            </button>
          )}
        </div>
      )}

      {order.awaiting_payment && (
        <div
          className={`rental-warn-box${order.awaiting_payment.overdue ? " rental-warn-box--orange" : " rental-warn-box--muted"}`}
          style={{ marginBottom: 12 }}
        >
          <strong>
            {order.awaiting_payment.overdue
              ? `⚠ 입금 확인 필요 — ${order.awaiting_payment.hours}시간 경과`
              : "입금대기 — 무통장 입금 전"}
          </strong>
          <div className="rental-warn-reason">
            입금 예정 {formatWon(order.awaiting_payment.amount_due)} · 날짜는 가예약으로 막혀 있습니다.
            카페24에서 입금이 확인되면 배송준비중으로 넘어가고 알림·캘린더가 그때 생깁니다.
            카페24에서 취소하면 ERP도 취소로 따라가 날짜가 풀립니다.
          </div>
        </div>
      )}

      {order.status === "CANCELED" && (
        <div className="rental-warn-box" style={{ marginBottom: 12 }}>
          <strong>이 주문은 취소되었습니다</strong>
          {order.cancel_detail ? (
            <div className="rental-warn-reason">{order.cancel_detail}</div>
          ) : null}
        </div>
      )}

      {/* 상세에서는 사유 전문을 보여준다(카드는 한 줄 요약만) */}
      {order.needs_manual_refund && (
        <div className="rental-warn-box" style={{ marginBottom: 12 }}>
          <strong>
            ⚠ 카페24 환불 수동처리 필요 · 환불액 {formatWon(order.refund_amount)}
            {formatRefundBreakdown(order) ? ` (${formatRefundBreakdown(order)})` : ""}
          </strong>
          {/* 결제수단별 처리 방법(회수완료 화면과 같은 문구) */}
          {Number(order.refund_amount) > 0 && (
            <div className="rental-refund-guide">{refundGuide(order, order.refund_amount)}</div>
          )}
          {order.manual_refund_reason ? (
            <div className="rental-warn-reason">{order.manual_refund_reason}</div>
          ) : null}
        </div>
      )}

      <div className="rental-detail-grid">
        {/* ── 왼쪽: 세팅 체크리스트 + 전환 ── */}
        <div className="rental-section">
          {order.product_images?.length > 0 && (
            <div className="rental-thumbs">
              {order.product_images.map((image) => (
                <img
                  key={image.product_no}
                  className="rental-thumb"
                  src={image.image_url}
                  alt={image.product_name || "상품 이미지"}
                />
              ))}
            </div>
          )}

          <h3 className="rental-section-title">세팅 체크리스트</h3>
          {/* 배송 준비 수량 — 쌍둥이 추가분을 합친 값(서버 options.kit_summary) */}
          {(order.kit_lines || []).length > 0 && (
            <div className="rental-row">
              <span className="rental-row-label">준비 수량</span>
              <span className="rental-row-value">{order.kit_lines.join(" · ")}</span>
            </div>
          )}
          {/* 쌍둥이·추가 주문 경고(serializers.twin_warnings) — 자동으로 고치지 않는다 */}
          {(order.twin_warnings || []).map((warning) => (
            <div key={warning} className="rental-warn-box" style={{ marginBottom: 12 }}>
              <strong>⚠ {warning}</strong>
            </div>
          ))}
          <AddonLinkPicker order={order} onLinked={load} />
          {(order.checklist || []).map((group, index) => (
            <div key={`${group.product_no}-${index}`} style={{ marginBottom: 12 }}>
              <div className="rental-row">
                <span className="rental-row-value" style={{ fontWeight: 700 }}>
                  {group.product_name}
                </span>
              </div>
              <ul className="rental-checklist">
                {group.options.map((option) => {
                  const checked = Boolean((order.items_checked || {})[option.slug]);
                  return (
                    <li
                      key={option.slug}
                      className={`rental-opt rental-opt--${option.slug}${
                        option.is_none ? " rental-opt--none" : ""
                      }`}
                      data-option={option.slug}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOption(option.slug)}
                        id={`opt-${option.slug}`}
                      />
                      <label htmlFor={`opt-${option.slug}`} className="rental-opt-name">
                        {option.name || "항목"}
                      </label>
                      <span className="rental-opt-value">{option.value || "-"}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {!order.is_addon && <HandoverProgress handover={order.handover} />}

          <div className="rental-card-actions" style={{ marginTop: 10 }}>
            {order.is_addon ? (
              // 추가 주문 — 단계 버튼 없음. 원 주문에서 진행하면 같이 넘어간다.
              <span className="rental-addon-follow">원 주문 상태를 따라갑니다</span>
            ) : order.handover?.needed ? (
              // 직접 수령·반납 — 송장 발행·단계 버튼 대신 한 번에 처리한다.
              <button
                type="button"
                className="rental-btn-next rental-btn-handover"
                onClick={handover}
                disabled={busy}
              >
                {busy ? "처리 중…" : handoverLabel(order)}
              </button>
            ) : order.needs_tracking_issue ? (
              <button
                type="button"
                className="rental-btn-next rental-btn-issue"
                onClick={issueTracking}
                disabled={busy}
              >
                {busy ? "발행 중…" : "송장 발행"}
              </button>
            ) : (
              <button
                type="button"
                className={`rental-btn-next rental-btn-next--${order.next_status || order.status}`}
                onClick={handleNext}
                disabled={!order.next_status || busy}
              >
                {order.next_status
                  ? (busy ? "처리 중…" : order.next_status_label)
                  : order.awaiting_payment ? "입금대기" : "완료됨"}
              </button>
            )}
          </div>
          {order.needs_tracking_issue && (
            <div className="rental-auto-hint" style={{ marginTop: 6, display: "block" }}>
              송장을 발행하면 카페24가 배송대기로 바뀌고, 그다음 배송출발로 넘어갈 수 있습니다.
            </div>
          )}
        </div>

        {/* ── 오른쪽: 정보 + 타임라인 + 메모 ── */}
        <div>
          <div className="rental-section" style={{ marginBottom: 14 }}>
            <h3 className="rental-section-title">배송 정보</h3>
            <div className="rental-row">
              <span className="rental-row-label">주소</span>
              <span className="rental-row-value">
                ({order.receiver_zipcode}) {order.receiver_address} {order.receiver_address_detail}
              </span>
              <button type="button" className="rental-btn-copy" onClick={copyAddress}>
                복사
              </button>
            </div>
            {/* 공동현관 비밀번호 — 카페24 주문서 추가항목. 회수완료·취소면 서버가 보내지 않는다.
                원 주문이면 붙은 추가 주문의 값도 합쳐 온다(번호가 다를 때만 출처를 붙인다). */}
            {(order.door_codes || []).map((door) => (
              <div className="rental-door-code" key={`${door.source}-${door.value}`}>
                공동현관 비밀번호: {door.value}
                {door.source && <span className="rental-door-source"> ({door.source})</span>}
              </div>
            ))}
            <div className="rental-row">
              <span className="rental-row-label">연락처</span>
              <span className="rental-row-value">
                {order.receiver_phone ? (
                  <a href={`tel:${order.receiver_phone}`}>{order.receiver_phone}</a>
                ) : (
                  "-"
                )}
              </span>
            </div>
            {/* 상품 — 이름·유형은 서버(상품 설정 RentalProduct)가 정한다 */}
            <div className="rental-row">
              <span className="rental-row-label">상품</span>
              <span className="rental-row-value">
                {order.product_name || "-"}
                {/* 주문마다 다른 대여 기간(아기침대) */}
                {order.rental_period ? ` · ${order.rental_period}` : ""}
              </span>
            </div>
            {/* 추가 주문 ↔ 원 주문 */}
            {order.is_addon && (
              <div className="rental-row">
                <span className="rental-row-label">원 주문</span>
                <span className="rental-row-value">
                  {order.parent_info ? (
                    <span
                      className="rental-link"
                      role="link"
                      onClick={() => navigate(`/rental/orders/${order.parent_info.id}`)}
                    >
                      {order.parent_info.order_id} ({order.parent_info.status_label})
                    </span>
                  ) : (
                    "미연결"
                  )}
                </span>
              </div>
            )}
            {(order.addon_orders_info || []).map((addon) => (
              <div className="rental-row" key={addon.id}>
                <span className="rental-row-label">추가 주문</span>
                <span className="rental-row-value">
                  <span
                    className="rental-link"
                    role="link"
                    onClick={() => navigate(`/rental/orders/${addon.id}`)}
                  >
                    {addon.order_id}
                  </span>
                  ,{" "}
                  {formatWon(addon.amount)}
                  {addon.status === "CANCELED" ? " · 취소" : ""}
                </span>
              </div>
            ))}
            <div className="rental-row">
              <span className="rental-row-label">주문일시</span>
              <span className="rental-row-value">{formatDateTime(order.ordered_at)}</span>
            </div>
            <div className="rental-row">
              <span className="rental-row-label">결제일시</span>
              <span className="rental-row-value">
                {order.awaiting_payment ? "입금 전" : formatDateTime(order.paid_at)}
              </span>
            </div>
            {/* 수령·반납 방법 — 목록 카드와 같은 두 줄 표기(FulfillmentRows) */}
            <div className="rental-row">
              <span className="rental-row-label">수령·반납</span>
              <div className="rental-row-value">
                <ul className="rental-card-options">
                  <FulfillmentRows fulfillment={order.fulfillment} order={order} />
                </ul>
                {/* 담당자 수동 변경 — 옵션 판정보다 우선. 서버 응답으로 주문을 갈아 끼운다
                    (카드 표기·캘린더 제목·환불 기준액이 함께 바뀐 값). */}
                <FulfillmentEditor order={order} onSaved={setOrder} />
              </div>
            </div>
            {/* 배송메시지 — 카페24 원본 그대로, 길면 줄바꿈해서 전부 */}
            <div className="rental-row rental-card__message">
              <span className="rental-row-label">배송메시지</span>
              <span className="rental-row-value rental-card__message-text">
                {order.shipping_message || "-"}
              </span>
            </div>
            <div className="rental-row">
              <span className="rental-row-label">{eventDateLabel(order)}</span>
              <span className="rental-row-value">
                {order.event_date || "-"}
                {order.event_date_manual && (
                  <span className="rental-manual-badge">수동 지정</span>
                )}
              </span>
            </div>
            <div className="rental-row">
              <span className="rental-row-label">
                {scheduleLabels(order).out}·{scheduleLabels(order).back.replace("방문 ", "")}
              </span>
              {/* 추가 주문은 일정·송장이 없다 — 원 주문을 따른다 */}
              {order.is_addon ? (
                <span className="rental-row-value">원 주문 일정을 따릅니다</span>
              ) : (
              <span className="rental-row-value">
                <span className={order.delivery_delayed ? "rental-date--late" : undefined}>
                  {order.delivery_date || "-"}
                  {order.delivery_delayed && <span className="rental-late-badge">지연</span>}
                </span>
                {" / "}
                <span className={order.pickup_delayed ? "rental-date--late" : undefined}>
                  {order.pickup_date || "-"}
                  {order.pickup_delayed && <span className="rental-late-badge">지연</span>}
                </span>
                <span className="rental-auto-hint">아래 "일정"에서 수정</span>
              </span>
              )}
            </div>
            <div className="rental-row">
              <span className="rental-row-label">송장</span>
              {order.is_addon ? (
                <span className="rental-row-value">원 주문 일정을 따릅니다</span>
              ) : (
              <span className="rental-row-value">
                {order.tracking_no ||
                  (order.fulfillment === "direct"
                    ? "미등록 — [수령 완료]가 등록합니다"
                    : "미발행 — 배송준비중에서 [송장 발행]")}
                {order.cafe24_tracking_note && (
                  <span className="rental-auto-hint">{order.cafe24_tracking_note}</span>
                )}
              </span>
              )}
            </div>
          </div>

          {/* 추가 주문은 일정이 없다(서버도 막는다) */}
          {!order.is_addon && <DateEditor order={order} busy={busy} onSave={saveDates} />}

          <div className="rental-section" style={{ marginBottom: 14 }}>
            <h3 className="rental-section-title">금액</h3>
            <div className="rental-row">
              <span className="rental-row-label">결제금액</span>
              {/* 카페24 원본 실결제금액 — 목록 카드와 같은 값(payment_amount) */}
              <span className="rental-row-value rental-card__amount">
                {order.awaiting_payment ? (
                  <span className="rental-awaiting-amount">
                    입금 예정 {formatWon(order.awaiting_payment.amount_due)}
                  </span>
                ) : (
                  <strong>{formatWon(order.payment_amount)}</strong>
                )}
                {order.payment_change && (
                  <span className="rental-card__amount-change">
                    ({formatPaymentChange(order.payment_change)})
                  </span>
                )}
              </span>
            </div>
            <div className="rental-row">
              <span className="rental-row-label">배송비</span>
              <span className="rental-row-value">{formatWon(order.shipping_fee)}</span>
            </div>
            <div className="rental-row">
              <span className="rental-row-label">보증금</span>
              <span className="rental-row-value">{formatWon(order.deposit)}</span>
            </div>
            {order.refund_amount !== null && order.refund_amount !== undefined && (
              <div className="rental-row">
                <span className="rental-row-label">환불액</span>
                <span className="rental-row-value">
                  {formatWon(order.refund_amount)}
                  {Number(order.deduction_amount) > 0
                    ? ` (차감 ${formatWon(order.deduction_amount)})`
                    : ""}
                </span>
              </div>
            )}
            <div className="rental-row">
              <span className="rental-row-label">주문번호</span>
              <span className="rental-row-value">
                {order.order_id} · 카페24 {order.cafe24_status || "-"}
              </span>
            </div>
          </div>

          {/* 환불 처리 기록 — 매출 현황 환불 집계의 근거(2026-10-16) */}
          <RefundSection order={order} onSaved={setOrder} />

          <div className="rental-section" style={{ marginBottom: 14 }}>
            <h3 className="rental-section-title">진행 이력</h3>
            <ul className="rental-timeline">
              {(order.logs || []).map((log) => (
                <li key={log.id}>
                  <span className="rental-timeline-at">{formatDateTime(log.at)}</span>
                  <span>
                    {log.from_status_label ? `${log.from_status_label} → ` : ""}
                    {log.to_status_label}
                  </span>
                  <span className="rental-timeline-at">{log.actor_name}</span>
                  {(log.note || log.cafe24_summary) && (
                    <span className="rental-timeline-note">
                      {log.note}
                      {log.note && log.cafe24_summary ? " · " : ""}
                      {log.cafe24_summary}
                    </span>
                  )}
                </li>
              ))}
              {(order.logs || []).length === 0 && (
                <li>
                  <span className="rental-timeline-at">아직 전환 이력이 없습니다.</span>
                </li>
              )}
            </ul>
          </div>

          <div className="rental-section">
            <h3 className="rental-section-title">메모 (ERP 전용)</h3>
            {/* 목록 카드와 같은 컴포넌트 — 저장 결과(서버 응답)로 주문을 갈아 끼운다 */}
            <RentalMemo order={order} onSaved={setOrder} />
          </div>
        </div>
      </div>

      {showResolve && (
        <CancelResolveModal
          order={order}
          busy={busy}
          onClose={() => setShowResolve(false)}
          onConfirm={resolveCancel}
        />
      )}

      {showPickup && (
        <PickupModal
          order={order}
          busy={busy}
          onClose={() => setShowPickup(false)}
          onConfirm={(payload) => runTransition({ next_status: PICKED_UP, ...payload })}
        />
      )}
    </div>
  );
}
